import classNames from "classnames";
import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import { VideoPlaybackWithTrack } from "@fidget/common/components/call/videoPlayback";
import { L10n } from "@fidget/common/i18n";
import { IBrowserWindowSize } from "@fidget/common/models/browserWindowSize";
import { useStore } from "../../models";
import styles from "./agentVideoSection.css";
import { CobrowsingIframe } from "./cobrowsingIframe";

interface Props {
  hideVideo: boolean;
  videoExpanded: boolean;
  shouldRenderCobrowsing?: boolean;
  onlyCobrowsing: boolean;
}

const DEFAULT_VIDEO_SIZE: IBrowserWindowSize = { width: 870, height: 480 };

export const AgentVideoSection = observer(
  ({ hideVideo, videoExpanded, shouldRenderCobrowsing, onlyCobrowsing }: Props) => {
    const {
      shouldRenderBigVideoAndAudio,
      shouldRenderBigVideo,
      isBigVideoAudioDisabled,
      shouldRenderSmallVideoAndAudio,
      shouldRenderSmallVideo,
      isSmallVideoAudioDisabled,
      iframeSize,
      swapStreams,
      videoContainer,
      bigStream,
      smallStream
    } = agentVideoSectionState(hideVideo, videoExpanded);

    const renderBigVideo = () => {
      return (
        <div className={classNames(styles["video-wrapper"], styles["big-video-wrapper"])}>
          {(shouldRenderCobrowsing && <CobrowsingIframe size={iframeSize} />) ||
            (shouldRenderBigVideoAndAudio && <VideoPlaybackWithTrack track={bigStream.track!} />) ||
            (isBigVideoAudioDisabled && (
              <div className={styles["video-disabled"]}>
                <div className={styles["icons-container"]}>
                  <i className="icon-camera-crossed" />
                  <i className="icon-crossed-mic" />
                </div>
                {bigStream.allDisabledText}
              </div>
            )) || (
              <div className={styles["video-disabled"]}>
                <div className={styles["icons-container"]}>
                  <i className="icon-camera-crossed" />
                </div>
                {bigStream.cameraDisabledText}
              </div>
            )}
          {shouldRenderBigVideo && (
            <div className={styles["disabled-icon"]}>
              <i className="icon-crossed-mic" />
            </div>
          )}
        </div>
      );
    };

    const renderSmallVideo = () => {
      return (
        <div className={styles["rounded-video-container"]}>
          <div className={classNames(styles["video-wrapper"], styles["small-video-wrapper"])}>
            {(shouldRenderSmallVideoAndAudio && (
              <VideoPlaybackWithTrack track={smallStream.track!} />
            )) ||
              (isSmallVideoAudioDisabled && (
                <div className={styles["video-disabled"]}>
                  <div className={styles["icons-container"]}>
                    <i className="icon-camera-crossed" />
                    <i className="icon-crossed-mic" />
                  </div>
                  {smallStream.allDisabledText}
                </div>
              )) || (
                <div className={styles["video-disabled"]}>
                  <div className={styles["icons-container"]}>
                    <i className="icon-camera-crossed" />
                  </div>
                  {smallStream.cameraDisabledText}
                </div>
              )}
          </div>
          <div className={styles["swap-video"]} onClick={() => swapStreams()}>
            <div className={styles.icon}>
              <i className="icon-expand-video-icon" />
            </div>
          </div>
          {shouldRenderSmallVideo && (
            <div className={styles["disabled-icon"]}>
              <i className="icon-crossed-mic" />
            </div>
          )}
        </div>
      );
    };

    return (
      <div
        className={classNames(styles["video-container"], {
          [styles["client-camera-enabled"]]: bigStream.cameraEnabled,
          [styles["video-hidden"]]: hideVideo,
          [styles["video-expanded"]]: videoExpanded
        })}
        ref={videoContainer}
      >
        {renderBigVideo()}
        {!onlyCobrowsing && renderSmallVideo()}
      </div>
    );
  }
);

interface Stream {
  track?: MediaStreamTrack;
  cameraEnabled: boolean;
  microphoneEnabled: boolean;
  cameraDisabledText: JSX.Element;
  allDisabledText: JSX.Element;
}

interface AgentVideoSectionState {
  shouldRenderBigVideoAndAudio: boolean;
  shouldRenderBigVideo: boolean;
  isBigVideoAudioDisabled: boolean;
  shouldRenderSmallVideoAndAudio: boolean;
  shouldRenderSmallVideo: boolean;
  isSmallVideoAudioDisabled: boolean;
  iframeSize: IBrowserWindowSize;
  swapStreams: () => void;
  videoContainer: React.RefObject<HTMLDivElement>;
  bigStream: Stream;
  smallStream: Stream;
  remoteUserHtml?: string;
}

const agentVideoSectionState = (
  hideVideo: boolean,
  videoExpanded: boolean
): AgentVideoSectionState => {
  const getIframeSize = (): IBrowserWindowSize => {
    if (!videoContainer.current) {
      return DEFAULT_VIDEO_SIZE;
    }
    return {
      width: videoContainer.current.clientWidth,
      height: videoContainer.current.clientHeight
    };
  };

  const videoContainer = React.createRef<HTMLDivElement>();
  const { callStore } = useStore();
  const [streamSwapped, setStreamSwapped] = useState<boolean>(false);
  const [iframeSize, setIframeSize] = useState<IBrowserWindowSize>(getIframeSize());

  useEffect(() => {
    setIframeSize(getIframeSize());
  }, [videoExpanded, hideVideo]);

  const swapStreams = () => {
    setStreamSwapped(!streamSwapped);
  };

  const streams = {
    remoteStream: {
      track: callStore.remoteVideo.track,
      cameraEnabled: callStore.remoteVideo.isPlayable,
      microphoneEnabled: callStore.remoteAudio.isPlayable,
      cameraDisabledText: <L10n id={"header.call.clientDisabledCamera"} />,
      allDisabledText: <L10n id={"header.call.clientDisabledAll"} />
    },
    localStream: {
      track: callStore.localScreen.track || callStore.localCamera.track,
      cameraEnabled: callStore.localScreen.isPlayable || callStore.localCamera.isPlayable,
      microphoneEnabled: callStore.localMicrophone.isPlayable,
      cameraDisabledText: <L10n id={"header.call.yourCameraIsOff"} />,
      allDisabledText: <L10n id={"header.call.yourAllIsOff"} />
    }
  };

  const bigStream = streamSwapped ? streams.localStream : streams.remoteStream;
  const smallStream = streamSwapped ? streams.remoteStream : streams.localStream;

  const shouldRenderBigVideoAndAudio = bigStream.cameraEnabled && bigStream.track !== undefined;
  const shouldRenderBigVideo = bigStream.cameraEnabled && !bigStream.microphoneEnabled;
  const isBigVideoAudioDisabled = !bigStream.cameraEnabled && !bigStream.microphoneEnabled;
  const shouldRenderSmallVideoAndAudio =
    smallStream.cameraEnabled && smallStream.track !== undefined;
  const shouldRenderSmallVideo = smallStream.cameraEnabled && !smallStream.microphoneEnabled;
  const isSmallVideoAudioDisabled = !smallStream.cameraEnabled && !smallStream.microphoneEnabled;

  return {
    shouldRenderBigVideoAndAudio,
    shouldRenderBigVideo,
    isBigVideoAudioDisabled,
    shouldRenderSmallVideoAndAudio,
    shouldRenderSmallVideo,
    isSmallVideoAudioDisabled,
    iframeSize,
    swapStreams,
    videoContainer,
    bigStream,
    smallStream
  };
};
